import { useState, useEffect, useRef } from "react";
import { useSession } from "next-auth/client";
import { firebaseClient } from "firebaseClient";
import {
  HStack,
  Flex,
  Box,
  Button,
  Text,
  useColorModeValue,
  ButtonGroup,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { DateRange } from "react-date-range";
import { MdOutlineCalendarToday } from "react-icons/md";
import { ChevronDownIcon } from "@chakra-ui/icons";
import moment from "moment";
import { DateButton } from "./date-button";
import { useOnClickOutside } from "utils/helpers";
import { getAccount } from "utils/sessionHelper";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { AiOutlineLeft } from "react-icons/ai";

interface Props {
  onChange?: (obj?: any) => void;
  defaultSelection?: "1" | "7" | "30" | "90" | "180" | "customDate";
  show1dPicker?: boolean;
  show180dPicker?: boolean;
  disableTimeframe?: boolean;
  timeFrame?: any;
}

export function CustomDatePicker({
  onChange = () => { },
  defaultSelection = "7",
  show1dPicker = false,
  show180dPicker = false,
  disableTimeframe = false,
  timeFrame,
}: Props) {
  const [showPicker, setShowPicker] = useState<boolean>(false);
  const [selected, setSelected] = useState<string>(defaultSelection);
  const [selectedCustomDate, setSelectedCustomDate] = useState<any>();
  const [minDate, setMinDate] = useState(undefined);
  const [date, setDate] = useState([
    {
      startDate: moment().subtract(3, "day").startOf("day").toDate(),
      endDate: moment().subtract(1, "day").endOf("day").toDate(),
      key: "selection",
    },
  ]);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const dateRangeRef = useRef(null);
  const [session] = useSession();
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.400");
  const bgColor = useColorModeValue("white", "gray.600");

  useOnClickOutside(dateRangeRef, async () => {
    await new Promise((resolve) => setTimeout(resolve, 200));
    setShowPicker(false);
  });

  useEffect(() => {
    if (session && !hasFetched) {
      setHasFetched(true);
      const getMinDate = async () => {
        const db = firebaseClient.firestore();
        const docs = await db
          .collection("users")
          .doc(getAccount(session)?.id)
          .collection("accounts")
          .doc(getAccount(session)?.idAccount)
          .collection("accountStats")
          .orderBy("dateFetched", "asc")
          .limit(1)
          .get();

        const stats: any = [];
        docs.forEach((doc) => {
          stats.push(doc.data());
        });
        if (stats?.length > 0) {
          setMinDate(stats[0].dateFetched.toDate());
        }
      };

      getMinDate();
    }
  }, [session]);

  useEffect(() => {
    if (selected !== "customDate") {
      setDate([
        {
          startDate: moment().subtract(3, "day").startOf("day").toDate(),
          endDate: moment().subtract(1, "day").endOf("day").toDate(),
          key: "selection",
        },
      ]);
      setSelectedCustomDate(undefined);
    }
  }, [selected]);

  const handleChange = (type: string) => {
    let formattedDate: any;
    switch (type) {
      case "1":
        formattedDate = moment().subtract(1, "day").startOf("day");
        break;
      case "30":
        formattedDate = moment().subtract(30, "days").startOf("day");
        break;
      case "90":
        formattedDate = moment().subtract(90, "days").startOf("day");
        break;
      case "180":
        formattedDate = moment().subtract(180, "days").startOf("day");
        break;
      case "customDate":
        formattedDate = {
          start: moment(date[0].startDate).startOf("day"),
          end: moment(date[0].endDate).endOf("day"),
        };
        break;
      case "7":
      default:
        formattedDate = moment().subtract(7, "days").startOf("day");
        break;
    }
    setSelected(type);

    if (type === "customDate") {
      setShowPicker(false);
      setSelectedCustomDate(formattedDate);
      onChange({
        start: new Date(formattedDate.start),
        end: new Date(formattedDate.end),
      });
      return;
    }
    onChange(new Date(formattedDate));
  };

  const styleFor7d = show1dPicker
    ? {
      borderLeft: "0",
      borderRight: "0",
    }
    : {
      borderTopLeftRadius: "4px",
      borderBottomLeftRadius: "4px",
    };

  const goPreviousPeriod = () => {
    // slide the period to the previous one of the same length
    if (timeFrame.start) {
      var periodLength = moment(timeFrame.end).diff(moment(timeFrame.start), "days");
      var newPeriod = {
        start: moment(timeFrame.start).subtract(periodLength, "days").toDate(),
        end: moment(timeFrame.start).toDate(),
      }
    }
    else {
      var periodLength = moment().diff(moment(timeFrame), "days");
      var newPeriod = {
        start: moment(timeFrame).subtract(periodLength, "days").toDate(),
        end: moment(timeFrame).toDate(),
      }
    }
    // console.log('periodLength:', periodLength)
    // console.log('timeFrame:', timeFrame)
    // console.log('newPeriod:', newPeriod)
    onChange(newPeriod);
    setSelectedCustomDate(newPeriod);
    setDate([
      {
        startDate: newPeriod.start,
        endDate: newPeriod.end,
        key: "selection",
      },
    ]);
    setSelected("customDate");
  }

  return (
    // <HStack spacing="0" position="relative" boxShadow="lg">
    <Flex>
      {
        !!timeFrame && (
          <Tooltip label={"Go previous period"}>
            <IconButton
              aria-label="previsou-period"
              icon={<AiOutlineLeft fontSize="8px" />}
              size="sm"
              variant="outline"
              mr={2}
              onClick={goPreviousPeriod}
            />
          </Tooltip>
        )
      }
      <ButtonGroup variant="light" isAttached>

        {show1dPicker && (
          <DateButton
            content="1d"
            tooltipText="last 1 day"
            borderTopLeftRadius="4px"
            borderBottomLeftRadius="4px"
            isActive={selected === "1"}
            onClick={() => handleChange("1")}
          />
        )}
        <DateButton
          content="7d"
          tooltipText="last 7 days"
          {...styleFor7d}
          isActive={selected === "7"}
          onClick={() => handleChange("7")}
        />
        <DateButton
          content="30d"
          tooltipText="last 30 days"
          borderLeft="0"
          borderRight="0"
          isActive={selected === "30"}
          onClick={() => handleChange("30")}
        />
        <DateButton
          content="90d"
          tooltipText="last 90 days"
          isActive={selected === "90"}
          onClick={() => handleChange("90")}
        />
        {show180dPicker && (
          <DateButton
            content="180d"
            tooltipText="last 180 days"
            borderLeft="0"
            borderTopRightRadius="4px"
            borderBottomRightRadius="4px"
            isActive={selected === "180"}
            onClick={() => handleChange("180")}
          />
        )}
        {
          !disableTimeframe && (
            <DateButton
              content={
                <Flex alignItems="center">
                  {/* <MdOutlineCalendarToday /> */}
                  <svg width="14" height="14" viewBox="0 0 14 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2.5C13.0938 2.5 14 3.40625 14 4.5V14.5C14 15.625 13.0938 16.5 12 16.5H2C0.875 16.5 0 15.625 0 14.5V4.5C0 3.40625 0.875 2.5 2 2.5H3.25V1.25C3.25 0.84375 3.5625 0.5 4 0.5C4.40625 0.5 4.75 0.84375 4.75 1.25V2.5H9.25V1.25C9.25 0.84375 9.625 0.5 10 0.5C10.4062 0.5 10.75 0.84375 10.75 1.25V2.5H12ZM12 15C12.25 15 12.5 14.7812 12.5 14.5V6.5H1.5V14.5C1.5 14.7812 1.71875 15 2 15H12Z" fill="currentColor" />
                  </svg>
                  {selected === "customDate" && selectedCustomDate ? (
                    <Text ml="1">
                      {moment(selectedCustomDate.start).format("ll")} -{" "}
                      {moment(selectedCustomDate.end).format("ll")}
                    </Text>
                  ) : (
                    // <ChevronDownIcon />
                    <Box {...{ ml: 1, mr: 1.5 }}>
                      <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.51562 6.28906L6.53906 9.28906C6.375 9.42969 6.1875 9.5 6 9.5C5.78906 9.5 5.60156 9.42969 5.46094 9.28906L2.48438 6.28906C2.25 6.07812 2.17969 5.75 2.29688 5.46875C2.41406 5.1875 2.69531 5 3 5H8.97656C9.28125 5 9.53906 5.1875 9.65625 5.46875C9.77344 5.75 9.72656 6.07812 9.51562 6.28906Z" fill="currentColor" />
                      </svg>
                    </Box>
                  )}

                </Flex>
              }
              pl={3.5}
              onClick={() => {
                setShowPicker(true);
              }}
              isActive={selected === "customDate"}
              marginLeft={"-1px"}
              paddingRight="1"
              borderTopRightRadius="4px"
              borderBottomRightRadius="4px"
            />
          )
        }
        {showPicker && (
          <Box
            ref={dateRangeRef}
            position="absolute"
            top="8"
            right="0"
            border="1px"
            textAlign="end"
            borderColor={borderColor}
            borderRadius="10"
            backgroundColor={bgColor}
            zIndex="1"
          >
            <DateRange
              className="datePicker"
              ranges={date}
              // @ts-ignore
              onChange={(item) => setDate([item.selection])}
              maxDate={new Date()}
              minDate={minDate}
              showDateDisplay={false}
            />
            <Button
              mr="2.5"
              mb="2.5"
              mt="2"
              variant={"secondary"}
              size="sm"
              onClick={() => {
                handleChange("customDate");
              }}
            >
              Apply
            </Button>
          </Box>
        )}
        {/* </HStack> */}
      </ButtonGroup>
    </Flex>
  );
}
